/**
 * The mission as small steps, on the laptop's screen (the live mission's
 * "View mission"), in the case's colours like the briefing and the report.
 *
 * One step at a time: the step to do now is the card in the middle, big,
 * with one big Done under the thumb; what is done stacks above it, struck
 * through; what is next peeks below, so the end is always in sight.
 *
 * Over the top runs the track: a node per step from the accept (lit from
 * the start, so it never begins empty) to the lock at the end. Each Done
 * plays in order: the button takes the hit, a word stamps onto the card and
 * sparks lift off it, the card flies up into the done stack, the marker
 * travels the track to the next node (the line lighting behind it, the node
 * popping as it lands), the count ticks down, the next card rises, and the
 * handler says a line. The last Done lands on the lock: it shakes, springs
 * open, the screen flashes and gold paper falls. Then the stamp button
 * lights to report in. "All steps" keeps the list with a quiet Undo.
 * Progress is kept per mission (store/mission-progress).
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Check, Lock, LockOpen, X } from "lucide-react";
import type { Mission } from "@/features/missionkit/api/use-mission";
import type { Tier } from "@/features/funnel/types";
import { useMissionProgress } from "@/store/mission-progress";
import { missionCopy } from "./mission-copy";
import { paletteVars } from "./open-case";
import { cheerFor, progressKey, stepsFor } from "./mission-steps";

/** Nothing done yet: one array, so the store selector stays stable. */
const NONE: number[] = [];
/** The beats of a Done, in ms from the tap: the stamp lands at once, the
 *  card is away by a third of a second, the marker has landed by the
 *  half, and the next card is under the thumb inside a second. */
const FLY_AT = 300;
const LAND_AT = 560;
const SETTLE_AT = 880;
/** The marker's travel between nodes, after the card has flown. */
const TRAVEL_MS = 420;
/** The last step: the marker arrives on it and it pops. */
const ARRIVE_AT = FLY_AT + 10 + FLY_AT + TRAVEL_MS;
/** Then, the pop done, the marker sets straight off for the lock. */
const LOCK_AT = ARRIVE_AT + 220;
const LOCK_TRAVEL_MS = 520;
/** The marker reaches the lock: it opens, the flash, the paper. */
const OPEN_AT = LOCK_AT + LOCK_TRAVEL_MS;
/** The completion card springs in under the flash. */
const SETTLE_ALL_AT = OPEN_AT + 150;
/** How long the handler's line stays after the landing. */
const TOAST_MS = 1500;
/** The word stamped on each card, in turn: never the same twice running. */
const STAMP_WORDS = ["DONE", "NICE", "CLEAN", "BOLD", "SHARP"];
/** The sparks, each sent its own way off the stamp. */
const SPARKS = Array.from({ length: 12 }, (_, i) => {
  const a = (i / 12) * Math.PI * 2;
  const d = 52 + (i % 3) * 16;
  return { dx: Math.cos(a) * d, dy: Math.sin(a) * d };
});
/** Gold and red paper for the end, scattered the same way every time. */
const hash = (n: number) => {
  const v = Math.sin(n * 12.9898) * 43758.5453;
  return v - Math.floor(v);
};
const PAPER = Array.from({ length: 36 }, (_, i) => ({
  x: hash(i) * 100,
  delay: hash(i + 7) * 500,
  drift: (hash(i + 13) - 0.5) * 120,
  spin: (hash(i + 29) - 0.5) * 900,
  red: i % 4 === 0,
  w: 6 + hash(i + 41) * 6,
}));
const buzz = (p: number | number[]) => {
  try {
    navigator.vibrate?.(p);
  } catch {
    /* no vibration here */
  }
};

/**
 * The track: the accept, a node per step, the lock. The marker sits on the
 * last lit node and slides to the next on a Done; the line lights behind it.
 */
function Track({
  total,
  filled,
  landing,
  opened,
  marker: markerAt,
  travel,
  reduce,
}: {
  /** steps in the mission */
  total: number;
  /** nodes lit, the accept included */
  filled: number;
  /** the node just lit, popping */
  landing: number | null;
  /** the lock, open */
  opened: boolean;
  /** where the marker stands, when not on the first unlit node */
  marker?: number;
  /** the marker's travel: after a step's card has flown, or straight away */
  travel?: { ms: number; delay: number };
  reduce: boolean;
}) {
  const nodes = total + 2; // accept, the steps, the lock
  const at = (i: number) => `${(i / (nodes - 1)) * 100}%`;
  // the marker stands on the last lit node; on the lock once it is open
  // the marker stands on the node to do now (the first unlit), in ink so
  // it reads on every case's colour
  const marker = markerAt ?? Math.min(filled, nodes - 1);
  const { ms, delay } = travel ?? { ms: TRAVEL_MS, delay: FLY_AT };
  const move = reduce
    ? "none"
    : `width ${ms}ms cubic-bezier(.4,0,.2,1) ${delay}ms, left ${ms}ms cubic-bezier(.4,0,.2,1) ${delay}ms, opacity 300ms ease-out, scale 300ms ease-out`;
  return (
    <div
      role="progressbar"
      aria-label="Mission progress"
      aria-valuemin={0}
      aria-valuemax={nodes}
      aria-valuenow={filled}
      className="relative mx-2 h-9"
    >
      {/* the line, and the lit part of it */}
      <div className="absolute top-1/2 right-0 left-0 h-[4px] -translate-y-1/2 rounded-full bg-[color-mix(in_srgb,var(--ink)_14%,transparent)]" />
      <div
        style={{
          width: at(marker),
          transition: move,
        }}
        className="absolute top-1/2 left-0 h-[4px] -translate-y-1/2 rounded-full bg-[var(--ink)] shadow-[0_0_10px_color-mix(in_srgb,var(--glow)_80%,transparent)]"
      />
      {/* the nodes */}
      {Array.from({ length: nodes }, (_, i) => {
        const lit = i < filled;
        const isLock = i === nodes - 1;
        const pop = landing === i && !reduce;
        return (
          <span
            key={i}
            style={{ left: at(i) }}
            className={`absolute top-1/2 grid -translate-1/2 place-items-center rounded-full transition-colors duration-300 ${
              isLock
                ? `size-8 ${opened ? "bg-[#245c2f] text-[#f3ecd8]" : "bg-[color-mix(in_srgb,var(--ink)_14%,transparent)] text-[color-mix(in_srgb,var(--ink)_55%,transparent)]"}`
                : lit
                  ? "size-[22px] bg-[var(--ink)] text-[var(--glow)] shadow-[0_0_0_3px_var(--day)]"
                  : "size-[14px] border-2 border-[color-mix(in_srgb,var(--ink)_30%,transparent)] bg-[var(--day)]"
            } ${pop ? "sm-steps-node-pop" : ""} ${isLock && landing === i && !reduce ? "sm-steps-lock-open" : ""}`}
          >
            {isLock ? (
              opened ? (
                <LockOpen className="size-4" strokeWidth={2.5} />
              ) : (
                <Lock className="size-4" strokeWidth={2.5} />
              )
            ) : lit ? (
              // every step done gets its check; the one just landed draws
              // it in with a snap
              <Check
                className={`size-3.5 ${pop ? "sm-steps-check" : ""}`}
                strokeWidth={4}
              />
            ) : null}
          </span>
        );
      })}
      {/* the marker: their light, travelling the line */}
      <span
        style={{
          left: at(marker),
          transition: move,
        }}
        className={`pointer-events-none absolute top-1/2 size-[22px] -translate-1/2 rounded-full border-[3px] border-[var(--ink)] bg-[var(--paper)] transition-opacity duration-300 ${
          opened ? "scale-50 opacity-0" : "sm-steps-marker"
        }`}
      />
    </div>
  );
}

export function MissionSteps({
  tier,
  mission,
  handler,
  missionNo,
  steps: chainSteps,
  done: chainDone,
  onTick,
  settled = false,
  onStamp,
  onClose,
}: {
  tier: Tier;
  mission: Mission;
  /** who praises each step ("Your handler" until they have one) */
  handler: string | null;
  /** which mission this is (1 = their first), over the mission line */
  missionNo?: number;
  /** days in a row with a mission filed (0 or 1: nothing to say yet) */
  streak?: number;
  /** the adventure's chain, when it has one: the texts, which are done, and
   *  the moves (Done takes the branch; a tap on a done step goes back to it).
   *  Without them the deck keeps its own progress in the local store. */
  steps?: string[];
  done?: number[];
  onTick?: (step: number) => void;
  onUntick?: (step: number) => void;
  /** arrived here with the lock already open (the chain's stamp step,
   *  mounted as the last step's branch is taken): nothing springs in again */
  settled?: boolean;
  /** every step done, stamped: on to report in */
  onStamp: () => void;
  onClose: () => void;
}) {
  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const chained = !!chainSteps;
  const steps = chainSteps ?? stepsFor(mission);
  const key = progressKey(mission.missionId);
  const { headline } = missionCopy(mission);
  const localDone = useMissionProgress((s) => s.done[key]) ?? NONE;
  // the chain's last step counts as done here while its landing plays out;
  // the branch is taken once the lock has opened (the player would remount
  // this screen mid-flight otherwise)
  const [pending, setPending] = useState<number | null>(null);
  const baseDone = chainDone ?? localDone;
  const done =
    pending != null && !baseDone.includes(pending)
      ? [...baseDone, pending]
      : baseDone;
  const toggle = useMissionProgress((s) => s.toggle);
  const doneCount = steps.filter((_, i) => done.includes(i)).length;
  const all = doneCount === steps.length;
  const left = steps.length - doneCount;
  // the step to do now: the first not done
  const current = steps.findIndex((_, n) => !done.includes(n));
  // the track counts the accept as the first lit node, and the lock last
  const filled = doneCount + 1 + (all ? 1 : 0);
  // the last step, in legs: the marker lands on it, travels to the lock,
  // the lock opens. Until then the track shows the lock still shut.
  const [lockLeg, setLockLeg] = useState<"hold" | "travel" | "open" | null>(
    null,
  );
  const holding = all && (lockLeg === "hold" || lockLeg === "travel");
  const filledShown = all && lockLeg === "hold" ? doneCount + 1 : filled;
  const openedShown = all && !holding;
  const markerShown = all && lockLeg === "hold" ? steps.length : undefined;
  // the lock leg: the marker goes straight on, a little longer stretch
  const travel =
    all && (lockLeg === "travel" || lockLeg === "open")
      ? { ms: LOCK_TRAVEL_MS, delay: 0 }
      : undefined;

  // a Done playing out: the card being stamped (its word), then flying
  const [play, setPlay] = useState<{
    step: number;
    word: string;
    all: boolean;
    phase: "stamp" | "fly" | "land";
  } | null>(null);
  const [landing, setLanding] = useState<number | null>(null);
  const [cheer, setCheer] = useState("");
  const [paper, setPaper] = useState(false);
  // the stamp: pressed, the seal slams onto the card, then on to report in
  const [stamped, setStamped] = useState(false);
  const STAMP_HOLD = 950;
  const stampIt = () => {
    if (stamped) return;
    if (reduce) {
      onStamp();
      return;
    }
    setStamped(true);
    buzz([20, 40, 120]);
    later(onStamp, STAMP_HOLD);
  };
  const sealDate = new Date().toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach(window.clearTimeout), []);
  const later = (fn: () => void, ms: number) =>
    timers.current.push(window.setTimeout(fn, reduce ? 0 : ms));

  const finish = (i: number) => {
    if (play) return;
    const nowDone = doneCount + 1;
    const isAll = nowDone === steps.length;
    const word = isAll
      ? "MISSION COMPLETE"
      : STAMP_WORDS[doneCount % STAMP_WORDS.length];
    setPlay({ step: i, word, all: isAll, phase: "stamp" });
    if (isAll) setLockLeg("hold");
    buzz(isAll ? [40, 40, 90] : 18);
    later(() => setPlay((p) => p && { ...p, phase: "fly" }), FLY_AT);
    later(() => {
      // the card is in the stack; the marker sets off (the track reads the
      // store, or the chain's next step); the node it lands on pops as it
      // arrives
      if (chained && isAll) setPending(i);
      else if (chained) onTick?.(i);
      else toggle(key, i);
      setPlay((p) => p && { ...p, phase: "land" });
    }, FLY_AT + 10);
    // the node pops as the marker lands on it; on the last step the pop
    // waits for the marker to arrive, since the lock leg follows from it
    later(() => setLanding(nowDone), isAll ? ARRIVE_AT : LAND_AT + 200);
    if (isAll) {
      // the marker sets off along the last stretch, then the lock opens
      later(() => setLockLeg("travel"), LOCK_AT);
      later(() => {
        setLockLeg("open");
        setLanding(nowDone + 1);
        setPaper(true);
        buzz([30, 30, 30, 30, 120]);
      }, OPEN_AT);
    }
    const settle = isAll ? SETTLE_ALL_AT : SETTLE_AT;
    later(() => {
      setPlay(null);
      setCheer(cheerFor(nowDone, steps.length));
    }, settle);
    later(() => {
      setLanding(null);
      setCheer("");
    }, settle + TOAST_MS);
    later(() => {
      setPaper(false);
      // the paper down: now the chain takes its branch, the track as it is
      if (chained && isAll) onTick?.(i);
    }, settle + 1800);
  };
  // while the card is flying its step is already counted below it
  const showing = play && play.phase !== "stamp" ? play.step : current;
  const card = showing >= 0 ? steps[showing] : null;

  const vars = {
    ...paletteVars(tier),
    "--dock": "118px",
  } as CSSProperties;

  return (
    <div
      style={vars}
      className={`sm-steps absolute inset-0 flex flex-col overflow-hidden bg-[var(--day)] pt-[44px] text-[var(--ink)] ${
        stamped ? "sm-steps-thud" : ""
      }`}
    >
      {/* the end: a flash and gold paper */}
      {paper && !reduce ? (
        <>
          <div className="pointer-events-none absolute inset-0 z-[5] animate-[smStepsFlash_.5s_ease-out_both] bg-[var(--glow)]" />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[4] overflow-hidden"
          >
            {PAPER.map((p, i) => (
              <i
                key={i}
                style={
                  {
                    left: `${p.x}%`,
                    width: p.w,
                    height: p.w * 0.6,
                    animationDelay: `${p.delay}ms`,
                    "--drift": `${p.drift}px`,
                    "--spin": `${p.spin}deg`,
                    background: p.red ? "#a8271c" : "var(--ink)",
                  } as CSSProperties
                }
                className="sm-steps-paper"
              />
            ))}
          </div>
        </>
      ) : null}

      {/* the top: the mission in one line, and the track */}
      <div className="flex items-center justify-between px-5 pt-2">
        <span />
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-9 cursor-pointer place-items-center rounded-full bg-[color-mix(in_srgb,var(--ink)_10%,transparent)] text-[var(--ink)]"
          >
            <X className="size-5" />
          </button>
        </div>
      </div>
      <div className="px-5 pt-2">
        <div className="mt-2">
          <Track
            total={steps.length}
            filled={filledShown}
            landing={landing}
            opened={openedShown}
            marker={markerShown}
            travel={travel}
            reduce={reduce}
          />
        </div>
      </div>

      {
        <div className="relative grid min-h-0 flex-1 grid-rows-[1fr_auto_1fr] px-5 pt-3 pb-2">
          {/* the mission, centred between the track and the step */}
          <div className="flex min-h-0 flex-col items-center justify-center text-center">
            {missionNo ? (
              <div className="font-stencil text-[26px] leading-none tracking-[.08em] text-[var(--ink)] uppercase">
                Mission {missionNo}
              </div>
            ) : null}
            <p className="mt-2 text-[15px] leading-[1.35] font-medium text-[color-mix(in_srgb,var(--ink)_80%,transparent)] text-pretty [overflow-wrap:anywhere]">
              {headline}
            </p>
          </div>
          {/* the step to do now, at the centre of the screen; the stamp
              lands on it */}
          <div className="relative">
            {all && !play ? (
              <div
                className={`relative overflow-hidden rounded-[16px] px-2 py-6 text-center ${
                  stamped
                    ? "sm-steps-pressed"
                    : reduce || settled
                      ? ""
                      : "animate-[smStepsCardIn_.35s_cubic-bezier(.2,.8,.3,1)_both]"
                }`}
              >
                <div
                  className={`mx-auto grid size-16 place-items-center rounded-full bg-[#245c2f] text-[#f3ecd8] ${
                    reduce || settled
                      ? ""
                      : "animate-[smStepsNodePop_.45s_cubic-bezier(.2,1.5,.4,1)_.12s_both]"
                  }`}
                >
                  <LockOpen className="size-8" strokeWidth={2.5} />
                </div>
                <p className="mt-3 font-stencil text-[22px] leading-[1.05] tracking-[.04em] uppercase">
                  Mission complete
                </p>
                <p className="mt-1.5 text-[14px] text-[color-mix(in_srgb,var(--ink)_70%,transparent)]">
                  Stamp it. The next lock is waiting.
                </p>
                {/* the seal: red ink, slammed on, the date in it; ink spat
                    off the edges as it lands */}
                {stamped ? (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 grid place-items-center"
                  >
                    <div className="sm-steps-seal relative -rotate-[9deg] rounded-[8px] border-[4px] border-[#a8271c] px-4 py-2 text-center text-[#a8271c] [box-shadow:inset_0_0_0_2px_var(--paper),inset_0_0_0_4px_#a8271c]">
                      <div className="font-mono text-[9.5px] tracking-[.3em] uppercase">
                        Northstar agency
                      </div>
                      <div className="font-stencil text-[30px] leading-[1] tracking-[.08em] uppercase">
                        Complete
                      </div>
                      <div className="mt-0.5 font-mono text-[10px] tracking-[.2em] uppercase">
                        {sealDate}
                      </div>
                    </div>
                    {SPARKS.map((sp, n) => (
                      <i
                        key={n}
                        style={
                          {
                            "--dx": `${sp.dx * 1.6}px`,
                            "--dy": `${sp.dy * 1.2}px`,
                            animationDelay: `${180 + n * 12}ms`,
                            width: 4 + (n % 3) * 3,
                            height: 4 + (n % 3) * 3,
                          } as CSSProperties
                        }
                        className="sm-steps-ink"
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : card ? (
              <div
                key={showing}
                className={`relative overflow-hidden rounded-[16px] px-2 py-4 text-center ${
                  play && play.step === showing
                    ? play.phase === "stamp"
                      ? "sm-steps-shake"
                      : "sm-steps-fly"
                    : reduce
                      ? ""
                      : "animate-[smStepsCardIn_.32s_cubic-bezier(.2,.8,.3,1)_both]"
                }`}
              >
                <div className="mx-auto inline-block rounded-full bg-[var(--ink)] px-4 py-1.5 font-mono text-[13px] font-bold tracking-[.24em] text-[var(--glow)] uppercase">
                  Step {showing + 1} of {steps.length}
                </div>
                <p className="mt-4 text-[clamp(24px,7cqw,32px)] leading-[1.2] font-bold tracking-[-.01em] text-pretty">
                  {card}
                </p>
                {play && play.step === showing && !reduce ? (
                  <>
                    <span
                      aria-hidden="true"
                      className={`sm-steps-stamp pointer-events-none absolute top-1/2 left-1/2 -translate-1/2 -rotate-12 rounded-[6px] border-[3px] px-3 py-1 font-stencil tracking-[.1em] uppercase ${
                        play.all
                          ? "border-[#a8271c] text-[18px] text-[#a8271c]"
                          : "border-[#245c2f] text-[26px] text-[#245c2f]"
                      }`}
                    >
                      {play.word}
                    </span>
                    {play.phase === "stamp"
                      ? SPARKS.map((s, n) => (
                          <i
                            key={n}
                            style={
                              {
                                "--dx": `${s.dx}px`,
                                "--dy": `${s.dy}px`,
                                animationDelay: `${n * 18}ms`,
                              } as CSSProperties
                            }
                            className="sm-steps-spark"
                          />
                        ))
                      : null}
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
          <div />
        </div>
      }

      {/* the handler, as each step lands */}
      {cheer ? (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none absolute inset-x-5 bottom-[calc(var(--dock)+12px)] z-[3] animate-[smStepsToast_.25s_cubic-bezier(.2,.8,.3,1)_both] rounded-[10px] bg-[#f3ecd8] px-4 py-3 text-center text-[#2b2107] shadow-[0_18px_40px_rgba(0,0,0,.45)]"
        >
          <div className="font-mono text-[9.5px] tracking-[.24em] text-[#8a7350] uppercase">
            {handler ?? "Your handler"}
          </div>
          <div className="mt-0.5 text-[15px] leading-[1.3] font-semibold">
            {cheer}
          </div>
        </div>
      ) : null}

      {/* under the thumb: one big Done, or the stamp once every step is in */}
      <div className="shrink-0 border-t border-[color-mix(in_srgb,var(--ink)_14%,transparent)] bg-[var(--day)] px-5 pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
        {all && !play ? (
          <button
            type="button"
            onClick={stampIt}
            disabled={stamped || pending != null}
            className={`relative w-full cursor-pointer overflow-visible rounded-[16px] py-4 text-[18px] font-bold shadow-[0_10px_24px_rgba(0,0,0,.25)] transition-[transform,background-color,color] duration-300 active:scale-[.98] disabled:cursor-default ${
              stamped
                ? "sm-steps-stamp-btn bg-[#245c2f] text-[#f3ecd8]"
                : `bg-[#a8271c] text-[#f2dba6] ${reduce ? "" : "sm-steps-ready-red"}`
            }`}
          >
            {/* the rings, out from the press */}
            {stamped ? (
              <>
                <span className="sm-steps-ring pointer-events-none absolute inset-0 rounded-[16px] border-2 border-[#245c2f]" />
                <span
                  style={{ animationDelay: "160ms" }}
                  className="sm-steps-ring pointer-events-none absolute inset-0 rounded-[16px] border-2 border-[var(--glow)]"
                />
              </>
            ) : null}
            <span className="relative inline-flex items-center justify-center gap-2">
              {stamped ? (
                <>
                  <Check className="size-5" strokeWidth={3} />
                  Stamped. Reporting in
                </>
              ) : (
                "Stamp it complete"
              )}
            </span>
          </button>
        ) : (
          <button
            type="button"
            disabled={!!play || current < 0}
            onClick={() => finish(current)}
            aria-label={current >= 0 ? `Done: ${steps[current]}` : "Done"}
            className={`relative w-full cursor-pointer overflow-hidden rounded-[16px] border border-[#d6a94e]/35 bg-[linear-gradient(180deg,#2e261c_0%,#1a1410_55%,#120e0a_100%)] py-4 text-[18px] font-bold text-[#d6a94e] shadow-[inset_0_1px_0_rgba(255,236,180,.14),inset_0_-3px_0_rgba(0,0,0,.5),0_3px_0_#070503,0_10px_24px_rgba(0,0,0,.3)] transition-[translate,box-shadow] active:translate-y-[3px] active:shadow-[inset_0_1px_0_rgba(255,236,180,.14),0_0_0_#070503,0_4px_10px_rgba(0,0,0,.3)] disabled:cursor-default ${
              play && !reduce ? "sm-steps-hit" : ""
            }`}
          >
            {/* the hit: a gold sweep across the button */}
            {play && !reduce ? (
              <span
                aria-hidden="true"
                className="sm-steps-sweep pointer-events-none absolute inset-y-0 w-1/3 bg-[linear-gradient(90deg,transparent,color-mix(in_srgb,var(--glow)_45%,transparent),transparent)]"
              />
            ) : null}
            <span className="relative">
              {play
                ? play.phase === "stamp"
                  ? play.word
                  : play.all
                    ? "Lock open"
                    : `${left} to the lock`
                : "Done. Next."}
            </span>
          </button>
        )}
      </div>

      <style>{`
        .sm-steps-stamp{animation:smStepsStamp .32s cubic-bezier(.2,1.4,.4,1) both;background:color-mix(in srgb,var(--paper) 85%,transparent);box-shadow:0 6px 18px rgba(0,0,0,.2)}
        .sm-steps-shake{animation:smStepsShake .3s ease-out .03s both}
        .sm-steps-fly{animation:smStepsFly .3s cubic-bezier(.5,0,.8,.4) both;pointer-events:none}
        .sm-steps-spark{position:absolute;left:50%;top:50%;width:6px;height:6px;margin:-3px 0 0 -3px;border-radius:50%;background:var(--glow);box-shadow:0 0 8px 2px color-mix(in srgb,var(--glow) 80%,transparent);animation:smStepsSpark .7s cubic-bezier(.1,.7,.3,1) both;pointer-events:none}
        .sm-steps-ready{animation:smStepsReady 1.1s ease-in-out 3}
        .sm-steps-hit{animation:smStepsHit .4s cubic-bezier(.2,1.4,.4,1) both}
        .sm-steps-sweep{animation:smStepsSweep .55s ease-out .05s both}
        .sm-steps-marker{animation:smStepsBreathe 1.6s ease-in-out infinite}
        .sm-steps-node-pop{animation:smStepsNodePop .45s cubic-bezier(.2,1.5,.4,1) both}
        .sm-steps-check{animation:smStepsCheck .5s cubic-bezier(.2,1.6,.4,1) .12s both}
        @keyframes smStepsCheck{0%{opacity:0;scale:.2;rotate:-40deg}60%{opacity:1;scale:1.35;rotate:8deg}100%{opacity:1;scale:1;rotate:0deg}}
        .sm-steps-lock-open{animation:smStepsLockOpen .7s cubic-bezier(.2,.8,.3,1) both}
        .sm-steps-paper{position:absolute;top:-4%;border-radius:1px;opacity:0;animation:smStepsPaper 1.8s cubic-bezier(.3,.1,.6,1) both}
        @keyframes smStepsStamp{0%{opacity:0;scale:2.2;rotate:-4deg}70%{opacity:1;scale:.95;rotate:-13deg}100%{opacity:1;scale:1;rotate:-12deg}}
        @keyframes smStepsShake{0%{translate:0 0}25%{translate:-3px 2px}50%{translate:3px -1px}75%{translate:-1px 1px}100%{translate:0 0}}
        @keyframes smStepsFly{from{opacity:1;translate:0 0;scale:1}to{opacity:0;translate:0 -70px;scale:.6}}
        @keyframes smStepsSpark{0%{opacity:0;translate:0 0;scale:.5}20%{opacity:1}100%{opacity:0;translate:var(--dx) var(--dy);scale:1.1}}
        @keyframes smStepsCardIn{from{opacity:0;translate:0 34px;scale:.96}to{opacity:1;translate:0 0;scale:1}}
        @keyframes smStepsSettle{from{opacity:0;translate:0 10px;scale:.9}to{opacity:1;translate:0 0;scale:1}}
        @keyframes smStepsToast{from{opacity:0;translate:0 10px;scale:.96}to{opacity:1;translate:0 0;scale:1}}
        @keyframes smStepsReady{0%,100%{box-shadow:0 10px 24px rgba(0,0,0,.25)}50%{box-shadow:0 10px 24px rgba(0,0,0,.25),0 0 0 4px color-mix(in srgb,var(--glow) 45%,transparent),0 0 26px color-mix(in srgb,var(--glow) 70%,transparent)}}
        @keyframes smStepsHit{0%{scale:1}30%{scale:.94}100%{scale:1}}
        @keyframes smStepsSweep{from{left:-35%}to{left:110%}}
        @keyframes smStepsBreathe{0%,100%{box-shadow:0 0 0 0 color-mix(in srgb,var(--ink) 35%,transparent)}50%{box-shadow:0 0 0 7px color-mix(in srgb,var(--ink) 0%,transparent)}}
        @keyframes smStepsNodePop{0%{scale:1}40%{scale:1.7}100%{scale:1}}
        @keyframes smStepsLockOpen{0%{rotate:0deg;scale:1}15%{rotate:-14deg}30%{rotate:12deg}45%{rotate:-8deg;scale:1.3}60%{rotate:0deg;scale:1.6}100%{rotate:0deg;scale:1}}
        @keyframes smStepsNum{from{opacity:0;translate:0 -8px}to{opacity:1;translate:0 0}}
        @keyframes smStepsFlash{0%{opacity:.75}100%{opacity:0}}
        .sm-steps-ready-red{animation:smStepsReadyRed 1.3s ease-in-out infinite}
        .sm-steps-stamp-btn{animation:smStepsBtnPress .4s cubic-bezier(.2,1.4,.4,1) both}
        .sm-steps-ring{animation:smStepsRing .7s cubic-bezier(.1,.7,.3,1) both}
        .sm-steps-pressed{animation:smStepsPressed .4s cubic-bezier(.3,.9,.4,1) .06s both}
        .sm-steps-seal{animation:smStepsSeal .42s cubic-bezier(.3,1.6,.5,1) .08s both;background:color-mix(in srgb,#a8271c 8%,transparent);mix-blend-mode:multiply}
        .sm-steps-ink{position:absolute;left:50%;top:50%;border-radius:50%;background:#a8271c;opacity:0;animation:smStepsInk .6s cubic-bezier(.1,.7,.3,1) both;pointer-events:none}
        .sm-steps-thud{animation:smStepsThud .4s cubic-bezier(.2,.8,.3,1) .26s both}
        @keyframes smStepsReadyRed{0%,100%{box-shadow:0 10px 24px rgba(0,0,0,.25);scale:1}50%{box-shadow:0 10px 24px rgba(0,0,0,.25),0 0 0 5px color-mix(in srgb,#a8271c 35%,transparent),0 0 28px color-mix(in srgb,#a8271c 70%,transparent);scale:1.02}}
        @keyframes smStepsBtnPress{0%{scale:1}35%{scale:.9}70%{scale:1.04}100%{scale:1}}
        @keyframes smStepsRing{0%{opacity:.9;scale:1}100%{opacity:0;scale:1.35 1.9}}
        @keyframes smStepsPressed{0%{scale:1}40%{scale:.96;translate:0 4px}100%{scale:1;translate:0 0}}
        @keyframes smStepsSeal{0%{opacity:0;scale:3.4;rotate:-2deg;filter:blur(2px)}60%{opacity:1;scale:.94;rotate:-10deg;filter:none}100%{opacity:1;scale:1;rotate:-9deg}}
        @keyframes smStepsInk{0%{opacity:0;translate:0 0;scale:.4}25%{opacity:.9}100%{opacity:0;translate:var(--dx) var(--dy);scale:1}}
        @keyframes smStepsThud{0%{translate:0 0}20%{translate:0 6px}45%{translate:-2px -3px}70%{translate:1px 2px}100%{translate:0 0}}
        @keyframes smStepsPaper{0%{opacity:0;translate:0 0;rotate:0deg}8%{opacity:1}100%{opacity:0;translate:var(--drift) 110vh;rotate:var(--spin)}}
        @media (prefers-reduced-motion:reduce){.sm-steps *{animation:none !important;transition:none !important}}
      `}</style>
    </div>
  );
}
